import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, Building, Clock, User, MessageSquare, ExternalLink, MapPin } from 'lucide-react';
import { DateTimeInlineEditor } from './DateTimeInlineEditor';
import { derivePhones } from '@/utils/derivePhones';
import { CalendarDropdown } from './CalendarDropdown';
import { formatTimestamp, getActionDeadline, getManualDeadline, getActionDeadlineDate, getUrgencyFromAction, getSuggestedDueDate } from '@/lib/time';
import { format } from 'date-fns';

interface CallAction {
  id: string;
  type: string;
  confidence: number;
  phrase?: string;
  start_ms?: number;
  end_ms?: number;
  action_data?: Record<string, any>;
  processed?: boolean;
  created_at?: string;
  campaign_id?: string;
  call_id?: string;
  prospect_id?: string;
  user_id?: string;
  due_date?: string;
}

// Maintain backward compatibility with existing props
interface InboundContactSummaryProps {
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  contactCompany?: string;
  actions: CallAction[];
  processedActions?: CallAction[];
  onMarkActionDone: (actionId: string) => void;
  onToggleActionProcessed?: (actionId: string) => void;
  onUpdateDueDate?: (actionId: string, dueAt: string) => Promise<void>;
  onOpenCallDetails?: (callId: string) => void;
}

export const InboundContactSummary: React.FC<InboundContactSummaryProps> = ({
  contactName,
  contactPhone,
  contactEmail,
  contactCompany,
  actions = [],
  processedActions = [],
  onMarkActionDone,
  onToggleActionProcessed,
  onUpdateDueDate,
  onOpenCallDetails
}) => {
  const [isUpdatingDueDate, setIsUpdatingDueDate] = useState(false);

  // Extract enhanced data from action_data - look in both message_taking and follow_up_call
  const getEnhancedData = () => {
    const relevantActions = actions.filter(action => 
      action.type === 'message_taking' || action.type === 'follow_up_call'
    );
    if (relevantActions.length === 0) return {};

    // Sort by creation time, newest first
    const sortedActions = relevantActions.sort((a, b) => 
      new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );

    // Prioritize actions that actually have summary data
    const actionWithSummary = sortedActions.find(action => 
      action.action_data?.caller_summary && action.action_data.caller_summary.trim().length > 0
    );

    return (actionWithSummary || sortedActions[0])?.action_data || {};
  };

  const enhancedData = getEnhancedData();
  
  // Get call timestamp from the earliest action
  const callTimestamp = actions.length > 0 ? 
    actions.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())[0]?.created_at 
    : null;
  
  // Use enhanced data or fallback to contact info
  const callerName = enhancedData.caller_name || contactName || 'Unknown';
  const callerSummary = enhancedData.caller_summary || '';
  const callbackNumber = enhancedData.callback_number || contactPhone || '';
  const urgency = enhancedData.urgency || '';
  const callerAddress = enhancedData.caller_address || '';

  // Get phone numbers and labels using actions array
  const phoneInfo = derivePhones(actions, contactPhone);
  
  // Find the primary action (highest priority/confidence non-processed action)
  const primaryAction = actions
    .filter(action => !action.processed)
    .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))[0];
  
  // Get urgency and suggested due date for primary action
  const actionUrgency = primaryAction ? getUrgencyFromAction(primaryAction) : null;
  const suggestedDueDate = primaryAction ? getSuggestedDueDate(primaryAction.created_at || new Date().toISOString(), actionUrgency) : null;
  
  // Calculate deadline - prefer action_data.due_at, then manual due_date, fall back to created_at + 24h
  const deadline = primaryAction?.action_data?.due_at || primaryAction?.due_date || 
    (primaryAction?.created_at ? new Date(new Date(primaryAction.created_at).getTime() + 24 * 60 * 60 * 1000).toISOString() : null);

  const handleUpdateDueDate = async (newDate: string) => {
    if (!primaryAction) return;
    
    setIsUpdatingDueDate(true);
    try {
      await onUpdateDueDate?.(primaryAction.id, newDate);
    } finally {
      setIsUpdatingDueDate(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      {/* Contact Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold text-lg">{callerName}</h3>
            {urgency && (
              <Badge 
                variant={urgency === 'ASAP' ? 'destructive' : urgency === 'Today' ? 'secondary' : 'outline'} 
                className={`ml-2 ${
                  urgency === 'ASAP' ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' :
                  urgency === 'Today' ? 'bg-orange-500 hover:bg-orange-600 text-white' :
                  urgency === 'Tomorrow' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' :
                  'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {urgency.toUpperCase()}
              </Badge>
            )}
          </div>
          
          {/* Call Timestamp */}
          {callTimestamp && (
            <div className="text-xs text-muted-foreground">
              Call received: {formatTimestamp(callTimestamp)}
            </div>
          )}
          
          {/* Primary Callback */}
          <div className="space-y-1">
            {callbackNumber && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">Primary Callback:</span>
                <span>{callbackNumber}</span>
              </div>
            )}
            {phoneInfo.callerId && phoneInfo.callerId !== phoneInfo.primary && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">Caller ID:</span>
                <span>{phoneInfo.callerId}</span>
              </div>
            )}
          </div>

          {/* Email and Company */}
          {contactEmail && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <span>{contactEmail}</span>
            </div>
          )}
          {contactCompany && (
            <div className="flex items-center gap-2 text-sm">
              <Building className="h-3 w-3 text-muted-foreground" />
              <span>{contactCompany}</span>
            </div>
          )}
          
          {/* Address/Location from action data */}
          {(() => {
            const locationAction = actions.find(a => a.action_data?.location || a.action_data?.address || a.action_data?.zip_code);
            if (locationAction) {
              const location = locationAction.action_data.location || 
                             locationAction.action_data.address || 
                             `Zip: ${locationAction.action_data.zip_code}`;
              return (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span>{location}</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
        
        {/* No action buttons in expanded header - they're in the bottom Actions section */}
      </div>

      {/* Caller Summary - More prominent */}
      {callerSummary && (
        <div className="border-t pt-4">
          <h4 className="font-semibold text-base mb-3 flex items-center gap-2 text-foreground">
            <MessageSquare className="h-4 w-4" />
            Call Summary
          </h4>
          <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg text-sm leading-relaxed font-medium">
            {callerSummary}
          </div>
        </div>
      )}

      {/* Route to */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-2">Route to</h4>
        <p className="text-sm text-muted-foreground">
          {(() => {
            const target = enhancedData.routing_target || extractRoutingTarget(actions);
            // Sanitize generic routing targets
            if (!target || target.toLowerCase().includes('any stage') || target.toLowerCase().includes('anyone')) {
              return 'team';
            }
            return target;
          })()}
        </p>
      </div>

      {/* Suggested Due Date for Urgent Calls */}
      {actionUrgency && suggestedDueDate && (
        <div className="border-t pt-4">
          <h4 className="font-medium mb-2">Suggested Due Date</h4>
          <div className="flex items-center gap-2">
            <Badge 
              variant={actionUrgency === 'ASAP' ? "destructive" : "secondary"}
              className={`text-sm font-semibold px-3 py-1 ${
                actionUrgency === 'ASAP' ? 'bg-red-500/15 text-red-800 border-red-300' :
                actionUrgency === 'Today' ? 'bg-orange-500/15 text-orange-800 border-orange-300' :
                'bg-blue-500/15 text-blue-800 border-blue-300'
              }`}
            >
              Due: {format(suggestedDueDate, 'EEE h:mm a')} ET
            </Badge>
          </div>
        </div>
      )}

      {/* Next Action Due */}
      {primaryAction && (
        <div className="border-t pt-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Next Action
          </h4>
          <div className="space-y-2 pl-6">
            {(() => {
              const deadline = primaryAction.action_data?.due_at 
                ? getManualDeadline(primaryAction.action_data.due_at)
                : getActionDeadline(primaryAction.type, primaryAction.created_at || new Date().toISOString(), 'inbound');
              
              const actionText = primaryAction.type === 'message_taking' 
                ? 'Message relay due:' 
                : primaryAction.type === 'follow_up_call' || primaryAction.type === 'consent_callback'
                ? 'Follow-up call due:'
                : 'Next action due:';
              
              const handleUpdateActionDueDate = async (newDueDate: Date) => {
                if (onUpdateDueDate) {
                  try {
                    await onUpdateDueDate(primaryAction.id, newDueDate.toISOString());
                  } catch (error) {
                    console.error('[InboundContactSummary] Due date update failed:', error);
                    throw error;
                  }
                }
              };
              
              return (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">⏰</span>
                    <span>{actionText}</span>
                    <span className={deadline.isOverdue ? "text-destructive font-medium" : "text-foreground"}>
                      {deadline.text}
                    </span>
                    {primaryAction.action_data?.due_at && (
                      <Badge variant="outline" className="text-xs px-1 py-0 ml-1">
                        Adjusted
                      </Badge>
                    )}
                    {onUpdateDueDate && (
                      <DateTimeInlineEditor 
                        currentDueDate={primaryAction.action_data?.due_at ? new Date(primaryAction.action_data.due_at) : new Date(Date.now() + 24 * 60 * 60 * 1000)}
                        onSave={handleUpdateActionDueDate}
                        isManuallyAdjusted={!!primaryAction.action_data?.due_at}
                      />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <CalendarDropdown
                      title={`Follow up: ${callerName}`}
                      description={`Follow up on inbound call. ${callerSummary ? `Message: ${callerSummary}` : ''} Contact: ${callbackNumber}${contactEmail ? `, ${contactEmail}` : ''}`}
                      startDate={primaryAction.action_data?.due_at ? new Date(primaryAction.action_data.due_at) : new Date(Date.now() + 24 * 60 * 60 * 1000)}
                      endDate={new Date((primaryAction.action_data?.due_at ? new Date(primaryAction.action_data.due_at) : new Date(Date.now() + 24 * 60 * 60 * 1000)).getTime() + 30 * 60 * 1000)}
                      location="Phone call"
                      className="h-7"
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </Card>
  );
};

// Helper functions
function extractRoutingTarget(actions: CallAction[]): string | null {
  for (const action of actions) {
    if (action.phrase) {
      // Look for mentions of names or departments
      const nameMatch = action.phrase.match(/(?:call|speak|talk to|reach|contact)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
      if (nameMatch) return nameMatch[1];
      
      // Look for departments
      const deptMatch = action.phrase.match(/(?:sales|support|service|manager|director|admin|accounting)/i);
      if (deptMatch) return deptMatch[0];
    }
    
    if (action.action_data?.routing_target) {
      return action.action_data.routing_target;
    }
  }
  return null;
}

function extractCallbackPreference(actions: CallAction[]): string | null {
  // First check for extracted urgency data
  for (const action of actions) {
    if (action.action_data?.urgency) {
      return action.action_data.urgency;
    }
  }
  
  // Fallback to phrase analysis
  for (const action of actions) {
    if (action.phrase) {
      if (/urgent|asap|immediately|right away/i.test(action.phrase)) {
        return 'ASAP';
      }
      if (/today|this morning|this afternoon/i.test(action.phrase)) {
        return 'Today';
      }
      if (/tomorrow|next business day/i.test(action.phrase)) {
        return 'Tomorrow';
      }
    }
  }
  
  return null;
}